/**
 * The input system. ARCHITECTURE.md §3, §5.
 *
 * Runs first in the tick order, so `ctx.input.players` is fully resolved before `combat-sim`
 * reads it. Three-free apart from the device adapters it delegates to.
 *
 * Latency path, in ticks (120 Hz), from physical press to move start:
 *   0   the browser delivers keydown; the adapter records the physical code
 *   0   this system polls the adapter during the same fixedUpdate and sets the press edge
 *   0   combat-sim (which depends on this system) reads the edge in the same tick
 * So a press is acted on within the tick it lands. The input BUFFER adds no latency — it only
 * lets an EARLY press still count, by keeping the edge alive for `inputBufferF60` frames.
 */

import { System, INPUT_BUFFER_TICKS, DisposalBag, f60 } from '../core/index.js';
import { BindingSet, ACTIONS } from './bindings.js';
import { KeyboardDevice } from './devices/keyboard.js';
import { GamepadDevice } from './devices/gamepad.js';
import { CommandRecogniser } from './buffer.js';
import {
  createInputSnapshot, clearEdges, resetSnapshot, packAndBuffer, describeSnapshot,
} from './snapshot.js';

/** Depth of the per-player history the training-mode input display reads. */
const HISTORY_DEPTH = 64;

export class InputSystem extends System {
  static id = 'core-input';
  static deps = [];
  static budgetMs = 0.1;

  constructor() {
    super();
    this.bag = new DisposalBag('input');
    this.bindings = null;
    this.keyboard = null;
    this.gamepad = null;
    this.recogniser = null;
    /** @type {Array<object>} */
    this.snapshots = [];
    /** Scratch action state, one per player, reused every tick. */
    this._raw = [makeRaw(), makeRaw()];
    /** Previous-tick held state, for edge detection. */
    this._prev = [makeRaw(), makeRaw()];
    /** Externally driven slots (AI, replay, tools) skip device polling. */
    this._synthetic = [null, null];
    /** Ring buffer of resolved inputs per player for the training display. */
    this._history = [];
    this._historyHead = [0, 0];
    this._lastFacing = [1, -1];
  }

  async init(ctx) {
    this.bindings = new BindingSet();
    ctx.input.bindings = this.bindings;

    this.keyboard = new KeyboardDevice(this.bindings);
    this.keyboard.attach(this.bag);
    this.gamepad = new GamepadDevice(this.bindings);
    this.gamepad.attach(this.bag);

    this.recogniser = new CommandRecogniser(2);
    this.snapshots = [createInputSnapshot(0), createInputSnapshot(1)];
    ctx.input.players = this.snapshots;

    for (let p = 0; p < 2; p++) {
      const ring = new Array(HISTORY_DEPTH);
      for (let i = 0; i < HISTORY_DEPTH; i++) {
        ring[i] = { tick: -1, dir: 5, mask: 0, command: '', text: '' };
      }
      this._history.push(ring);
    }

    ctx.log.info(
      `input: keyboard ${this.keyboard.available ? 'ready' : 'unavailable'}, ` +
        `gamepad ${this.gamepad.available ? 'ready' : 'unavailable'}`,
    );
  }

  fixedUpdate(step, ctx) {
    this.gamepad.poll();
    const fighters = ctx.sim.fighters;

    for (let p = 0; p < 2; p++) {
      const snap = this.snapshots[p];
      const raw = this._raw[p];
      const prev = this._prev[p];

      // --- carry forward last tick's held state for edge detection ---
      for (let i = 0; i < ACTIONS.length; i++) prev[ACTIONS[i]] = snap[ACTIONS[i]];
      clearEdges(snap);

      // --- resolve raw device (or synthetic) state ---
      const synth = this._synthetic[p];
      if (synth) {
        for (let i = 0; i < ACTIONS.length; i++) {
          const a = ACTIONS[i];
          raw[a] = synth[a] === true;
        }
        snap.synthetic = true;
        snap.device = 'synthetic';
      } else {
        snap.synthetic = false;
        // A gamepad takes over its slot when one is assigned; otherwise the keyboard drives it.
        if (this.gamepad.readInto(raw, p)) {
          snap.device = 'gamepad';
        } else {
          this.keyboard.readInto(raw, p);
          snap.device = 'keyboard';
        }
      }

      // --- screen-relative to facing-relative ---
      // Combat reasons about forward/back, never left/right, so the conversion happens exactly
      // once, here, using the facing the fighter had at the START of this tick.
      const facing = fighters[p] ? fighters[p].facing : p === 0 ? 1 : -1;
      if (facing !== this._lastFacing[p]) {
        // A cross-up mid-motion would otherwise invalidate a half-entered command.
        this.recogniser.mirrorHistory(p);
        this._lastFacing[p] = facing;
      }

      snap.up = raw.up;
      snap.down = raw.down;
      snap.left = raw.left;
      snap.right = raw.right;
      snap.fwd = facing > 0 ? raw.right : raw.left;
      snap.back = facing > 0 ? raw.left : raw.right;
      snap.lp = raw.lp;
      snap.hp = raw.hp;
      snap.lk = raw.lk;
      snap.hk = raw.hk;
      snap.sp = raw.sp;
      snap.guard = raw.guard;

      // --- edges ---
      edge(snap, prev, 'up', 'pressedUp', '');
      edge(snap, prev, 'down', 'pressedDown', '');
      edge(snap, prev, 'left', 'pressedLeft', '');
      edge(snap, prev, 'right', 'pressedRight', '');
      edge(snap, prev, 'fwd', 'pressedFwd', 'releasedFwd');
      edge(snap, prev, 'back', 'pressedBack', 'releasedBack');
      edge(snap, prev, 'lp', 'pressedLp', 'releasedLp');
      edge(snap, prev, 'hp', 'pressedHp', 'releasedHp');
      edge(snap, prev, 'lk', 'pressedLk', 'releasedLk');
      edge(snap, prev, 'hk', 'pressedHk', 'releasedHk');
      edge(snap, prev, 'sp', 'pressedSp', 'releasedSp');
      edge(snap, prev, 'guard', 'pressedGuard', 'releasedGuard');

      // --- held counters ---
      snap.heldFwd = snap.fwd ? snap.heldFwd + 1 : 0;
      snap.heldBack = snap.back ? snap.heldBack + 1 : 0;
      snap.heldUp = snap.up ? snap.heldUp + 1 : 0;
      snap.heldDown = snap.down ? snap.heldDown + 1 : 0;
      snap.heldLp = snap.lp ? snap.heldLp + 1 : 0;
      snap.heldHp = snap.hp ? snap.heldHp + 1 : 0;
      snap.heldLk = snap.lk ? snap.heldLk + 1 : 0;
      snap.heldHk = snap.hk ? snap.heldHk + 1 : 0;
      snap.heldSp = snap.sp ? snap.heldSp + 1 : 0;
      snap.heldGuard = snap.guard ? snap.heldGuard + 1 : 0;

      snap.tick = ctx.time.tick;
      packAndBuffer(snap); // also refreshes snap.dir

      // --- command recognition ---
      this.recogniser.push(p, snap.dir);
      const command = this.recogniser.recognise(p);
      if (command) {
        snap.command = command;
        snap.commandAge = 0;
        const payload = ctx.events.emit('input:command', this._commandPayload(ctx, p, command, snap));
        void payload;
      } else if (snap.commandAge < INPUT_BUFFER_TICKS) {
        snap.commandAge++;
      }

      this._record(p, snap);
    }
    void step;
  }

  _commandPayload(ctx, player, command, snap) {
    if (!this._cmdPayload) {
      this._cmdPayload = { tick: 0, player: 0, command: '', bufferedFrames: 0, direction: 5 };
    }
    const p = this._cmdPayload;
    p.tick = ctx.time.tick;
    p.player = player;
    p.command = command;
    p.bufferedFrames = this.recogniser.lastSpan;
    p.direction = snap.dir;
    return p;
  }

  _record(player, snap) {
    const ring = this._history[player];
    const slot = ring[this._historyHead[player]];
    slot.tick = snap.tick;
    slot.dir = snap.dir;
    slot.mask = snap.mask;
    slot.command = snap.command;
    slot.text = describeSnapshot(snap);
    this._historyHead[player] = (this._historyHead[player] + 1) % HISTORY_DEPTH;
  }

  // -------------------------------------------------------------------------
  // Public surface for AI, replay, tools and the training interface
  // -------------------------------------------------------------------------

  /**
   * Drives a slot externally. `partial` is a map of ACTIONS to booleans; omitted actions are
   * treated as released. Device polling is skipped for that slot until `clearSynthetic`.
   */
  setSyntheticInput(player, partial) {
    if (!this._synthetic[player]) this._synthetic[player] = makeRaw();
    const s = this._synthetic[player];
    for (let i = 0; i < ACTIONS.length; i++) {
      const a = ACTIONS[i];
      s[a] = partial ? partial[a] === true : false;
    }
    return s;
  }

  /** Merges into the existing synthetic state rather than replacing it. */
  patchSyntheticInput(player, partial) {
    if (!this._synthetic[player]) this._synthetic[player] = makeRaw();
    const s = this._synthetic[player];
    for (const k in partial) if (ACTIONS.includes(k)) s[k] = partial[k] === true;
    return s;
  }

  clearSynthetic(player) {
    this._synthetic[player] = null;
    resetSnapshot(this.snapshots[player]);
    this.recogniser.reset(player);
  }

  isSynthetic(player) {
    return this._synthetic[player] !== null;
  }

  /** Forces a recognised command on the next tick. Used by tools and the AI. */
  forceCommand(player, command) {
    this.snapshots[player].command = command;
    this.snapshots[player].commandAge = 0;
  }

  /**
   * Oldest-first resolved input history for the training display.
   * @param {number} player
   * @param {number} count
   */
  inputHistory(player, count = 16) {
    const ring = this._history[player];
    const head = this._historyHead[player];
    const out = [];
    const n = Math.min(count, HISTORY_DEPTH);
    for (let i = n; i > 0; i--) {
      const slot = ring[(head - i + HISTORY_DEPTH * 2) % HISTORY_DEPTH];
      if (slot.tick >= 0) out.push(slot);
    }
    return out;
  }

  /** Clears all held state. Called on round reset and when focus is lost. */
  resetAll() {
    for (let p = 0; p < 2; p++) {
      resetSnapshot(this.snapshots[p]);
      const prev = this._prev[p];
      for (let i = 0; i < ACTIONS.length; i++) prev[ACTIONS[i]] = false;
    }
    this.recogniser.reset(-1);
    if (this.keyboard) this.keyboard.releaseAll();
  }

  /** Effective buffer depth in ticks, from the user's setting. */
  bufferTicks(ctx) {
    return Math.min(INPUT_BUFFER_TICKS, f60(ctx.config.get('inputBufferF60')));
  }

  describeBindings() {
    return this.bindings.describeBindings();
  }

  dispose() {
    if (this.keyboard) this.keyboard.detach();
    if (this.gamepad) this.gamepad.detach();
    this.bag.disposeAll();
    this.snapshots = [];
    this._history = [];
    this.recogniser = null;
  }
}

function makeRaw() {
  const o = {};
  for (const a of ACTIONS) o[a] = false;
  return o;
}

function edge(snap, prev, key, pressedField, releasedField) {
  if (snap[key] && !prev[key]) snap[pressedField] = true;
  if (releasedField && !snap[key] && prev[key]) snap[releasedField] = true;
}
